import { utcToZonedTime } from 'date-fns-tz';
import Highcharts, {
  Options as HighchartsOptions,
  SeriesLineOptions,
  SeriesOptions,
  XAxisOptions,
  YAxisOptions,
} from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsBoost from 'highcharts/modules/boost';
import { isEmpty } from 'ramda';
import React, { memo, useRef } from 'react';
import ReactResizeDetector from 'react-resize-detector';

import { useAuth } from '../../../app/AuthorizationProvider';
import { Scalars } from '../../../graphql/components';
import { getZonedDateTimezoneOffset } from '../../../utils/date';
import { ACTIVE_PLOT_BAND_COLOR, BASE_SERIES_ID, GAPS_SERIES_ID, staticChartOptions } from '../config';
import {
  chartTooltipFormatter,
  chartXAxisLabelFormatter,
  chartYAxisLabelFormatter,
  generateChartGapSeriesOptions,
  getChartSeriesColor,
} from '../utils';

HighchartsBoost(Highcharts);

export interface AdjustmentRangeType {
  endDateTime?: number;
  startDateTime?: number;
}

export interface SeriesConfig {
  adjustmentId?: string | null;
  data: Scalars['ChartData'][];
  inCollision: boolean;
  inPreview: boolean;
  useGapConfig: boolean;
}

/* eslint-disable react/no-unused-prop-types */
export interface ChartProps {
  dataTestID: string;
  dateFormat: {
    full: string;
    short: string;
  };
  endDateTime: number;
  highlightedRange: AdjustmentRangeType;
  id: string;
  isSidebarOpen: boolean;
  labels: {
    xAxis: string;
    yAxis: string;
  };
  name: string;
  nameKey: string;
  seriesOptions: SeriesConfig[];
  timezone: string;
  valuePrecision?: number;
  localeObj?: Locale;
}
/* eslint-enable react/no-unused-prop-types */

const generateChartConfig: (
  props: ChartProps,
  baseSeriesId: string,
  gapsSeriesId: string,
  locale: string
) => HighchartsOptions = (
  {
    dateFormat,
    endDateTime,
    highlightedRange,
    id,
    isSidebarOpen,
    labels,
    name,
    nameKey,
    seriesOptions,
    timezone,
    valuePrecision,
    localeObj,
  },
  baseSeriesId,
  gapsSeriesId,
  locale
) => ({
  ...staticChartOptions,
  chart: {
    ...staticChartOptions.chart,
    height: isSidebarOpen ? 299 : staticChartOptions.chart!.height,
  },
  series: [
    ...seriesOptions.map(({ adjustmentId, data, inCollision, inPreview, useGapConfig }) =>
      useGapConfig
        ? generateChartGapSeriesOptions(data, baseSeriesId, gapsSeriesId)
        : {
            ...(staticChartOptions.series as SeriesOptions[])[0],
            data,
            name,
            nameKey,
            fillColor: getChartSeriesColor(inCollision, inPreview),
            id: adjustmentId ? `${id}_${adjustmentId}` : baseSeriesId,
            linkedTo: adjustmentId ? baseSeriesId : undefined,
            step: 'left' as SeriesLineOptions['step'],
            type: (adjustmentId ? 'area' : 'line') as SeriesLineOptions['type'],
          }
    ),
  ],
  time: {
    getTimezoneOffset: (timestamp: number) => getZonedDateTimezoneOffset(utcToZonedTime(timestamp, timezone), timezone),
    useUTC: true,
  },
  tooltip: {
    ...staticChartOptions.tooltip,
    formatter: chartTooltipFormatter(labels.xAxis, dateFormat.full, endDateTime, timezone, locale, valuePrecision),
  },
  xAxis: {
    ...staticChartOptions.xAxis,
    labels: {
      ...(staticChartOptions.xAxis as XAxisOptions).labels,
      formatter: chartXAxisLabelFormatter(dateFormat.short, timezone, localeObj),
    },
    plotBands: isEmpty(highlightedRange)
      ? []
      : [
          {
            color: ACTIVE_PLOT_BAND_COLOR,
            from: highlightedRange.startDateTime,
            to: highlightedRange.endDateTime,
          },
        ],
    title: {
      ...(staticChartOptions.xAxis as XAxisOptions).title,
      text: labels.xAxis,
    },
  },
  yAxis: {
    ...staticChartOptions.yAxis,
    labels: {
      ...(staticChartOptions.yAxis as YAxisOptions).labels,
      formatter: chartYAxisLabelFormatter(locale),
    },
    title: {
      ...(staticChartOptions.yAxis as YAxisOptions).title,
      text: labels.yAxis,
    },
  },
});

export const ChartComponent: React.FC<ChartProps> = (props) => {
  const { locale } = useAuth();
  const chartRef = useRef<HighchartsReact>(null);
  const { id, dataTestID } = props;

  const onResizeHandler = () => {
    if (chartRef && chartRef.current) {
      chartRef.current.chart.reflow();
    }
  };

  const baseSeriesId = `${id}_${BASE_SERIES_ID}`;
  const gapsSeriesId = `${id}_${GAPS_SERIES_ID}`;

  return (
    <ReactResizeDetector handleWidth onResize={onResizeHandler}>
      <div className="mb-5">
        <HighchartsReact
          data-test-id={dataTestID}
          highcharts={Highcharts}
          options={generateChartConfig(props, baseSeriesId, gapsSeriesId, locale)}
          ref={chartRef}
          updateArgs={[true, true, false]}
        />
      </div>
    </ReactResizeDetector>
  );
};

export const Chart = memo(ChartComponent);
